// One fetch of the product's autopilot bundle, shared by the Autopilot and
// Timeline tabs (both stay mounted behind display:none, so the modal owns
// the load and hands each tab the same data + reload).
import { useCallback, useEffect, useRef, useState } from 'react';
import axiosInstant from '../../../../api';
import { productAutopilotEndpoint } from '../../../../api/endpoints';
import { ProductAutopilot } from '../../../../types/autopilot';

export interface ProductAutopilotState {
  data: ProductAutopilot | null;
  loading: boolean;
  error: string | null;
  // Hard reload shows the loader; silent swaps the bundle in place.
  reload: (silent?: boolean) => Promise<void>;
}

export function useProductAutopilot(desireid: number | null | undefined, brandId: number | null): ProductAutopilotState {
  const [data, setData] = useState<ProductAutopilot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Guards a slow response landing after a newer request.
  const seq = useRef(0);

  const reload = useCallback(
    async (silent = false) => {
      if (!desireid) {
        setData(null);
        setLoading(false);
        return;
      }
      seq.current += 1;
      const mine = seq.current;
      if (!silent) setLoading(true);
      try {
        const { data: body } = await axiosInstant.get<ProductAutopilot>(productAutopilotEndpoint(desireid, brandId));
        if (mine !== seq.current) return;
        setData(body);
        setError(null);
      } catch (e: any) {
        if (mine !== seq.current) return;
        const status = e?.response?.status;
        if (status === 404) {
          // ProductHasNoPages: nothing to run autopilot on yet.
          setData(null);
          setError(null);
        } else if (!silent) {
          setError('Couldn\'t load Autopilot for this product. Please try again.');
        }
      } finally {
        if (mine === seq.current && !silent) setLoading(false);
      }
    },
    [desireid, brandId],
  );

  useEffect(() => {
    reload(false);
  }, [reload]);

  return { data, loading, error, reload };
}
